import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import { ChevronLeft, ChevronLeftCircle, ChevronRight, ChevronRightCircle, PanelLeftCloseIcon, PanelLeftIcon, PanelLeftOpenIcon, PanelRightOpenIcon } from "lucide-react";
import { useIsCompactViewport } from "@/hooks/use-device";
import { cn } from "@repo/blacksmith-ui/cn";
import { Button } from "@repo/blacksmith-ui/button";
import { Input } from "@repo/blacksmith-ui/input";
import { Separator } from "@repo/blacksmith-ui/separator";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@repo/blacksmith-ui/tooltip";
const SIDEBAR_COOKIE_NAME = "sidebar_state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "210px";
const SIDEBAR_WIDTH_ICON = "3rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";
type SidebarContextProps = {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
};
const SidebarContext = React.createContext<SidebarContextProps | null>(null);
function useSidebar() {
  const $ = _c(1);
  const context = React.useContext(SidebarContext);
  if (!context) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = {
        state: "expanded" as const,
        open: true,
        setOpen: _temp,
        openMobile: false,
        setOpenMobile: _temp2,
        isMobile: false,
        toggleSidebar: _temp3
      };
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  return context;
}
function _temp3() {}
function _temp2() {}
function _temp() {}
function SidebarProvider(t0) {
  const $ = _c(37);
  let children;
  let className;
  let openProp;
  let props;
  let setOpenProp;
  let style;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      defaultOpen: t1,
      open: openProp,
      onOpenChange: setOpenProp,
      enableKeyboardShortcut: t2,
      className,
      style,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = openProp;
    $[4] = props;
    $[5] = setOpenProp;
    $[6] = style;
    $[7] = t1;
    $[8] = t2;
  } else {
    children = $[1];
    className = $[2];
    openProp = $[3];
    props = $[4];
    setOpenProp = $[5];
    style = $[6];
    t1 = $[7];
    t2 = $[8];
  }
  const defaultOpen = t1 === undefined ? true : t1;
  const enableKeyboardShortcut = t2 === undefined ? true : t2;
  const isMobile = useIsCompactViewport();
  const [openMobile, setOpenMobile] = React.useState(false);
  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  let t3;
  if ($[9] !== open || $[10] !== setOpenProp) {
    t3 = value => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    };
    $[9] = open;
    $[10] = setOpenProp;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const setOpen = t3;
  let t4;
  if ($[12] !== setOpen) {
    t4 = () => setOpen(_temp4);
    $[12] = setOpen;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const toggleSidebar = t4;
  let t5;
  let t6;
  if ($[14] !== enableKeyboardShortcut || $[15] !== toggleSidebar) {
    t5 = () => {
      if (!enableKeyboardShortcut) {
        return;
      }
      const handleKeyDown = event => {
        if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey) && !event.shiftKey) {
          event.preventDefault();
          toggleSidebar();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t6 = [toggleSidebar, enableKeyboardShortcut];
    $[14] = enableKeyboardShortcut;
    $[15] = toggleSidebar;
    $[16] = t5;
    $[17] = t6;
  } else {
    t5 = $[16];
    t6 = $[17];
  }
  React.useEffect(t5, t6);
  const state = open ? "expanded" : "collapsed";
  let t7;
  if ($[18] !== isMobile || $[19] !== open || $[20] !== openMobile || $[21] !== setOpen || $[22] !== state || $[23] !== toggleSidebar) {
    t7 = {
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar
    };
    $[18] = isMobile;
    $[19] = open;
    $[20] = openMobile;
    $[21] = setOpen;
    $[22] = state;
    $[23] = toggleSidebar;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const contextValue = t7;
  let t8;
  if ($[25] !== style) {
    t8 = {
      "--sidebar-width": SIDEBAR_WIDTH,
      "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
      ...style
    };
    $[25] = style;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const t9 = t8 as React.CSSProperties;
  let t10;
  if ($[27] !== className) {
    t10 = cn("group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex h-full w-full overflow-hidden", className);
    $[27] = className;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== children || $[30] !== props || $[31] !== t10 || $[32] !== t9) {
    t11 = <TooltipProvider delayDuration={0}><div data-slot="sidebar-wrapper" style={t9} className={t10} {...props}>{children}</div></TooltipProvider>;
    $[29] = children;
    $[30] = props;
    $[31] = t10;
    $[32] = t9;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== contextValue || $[35] !== t11) {
    t12 = <SidebarContext.Provider value={contextValue}>{t11}</SidebarContext.Provider>;
    $[34] = contextValue;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  return t12;
}
function _temp4(open_0) {
  return !open_0;
}
function Sidebar(t0) {
  const $ = _c(34);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      side: t1,
      variant: t2,
      collapsible: t3,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  const side = t1 === undefined ? "left" : t1;
  const variant = t2 === undefined ? "sidebar" : t2;
  const collapsible = t3 === undefined ? "offcanvas" : t3;
  const {
    state
  } = useSidebar();
  if (collapsible === "none") {
    let t4;
    if ($[7] !== className) {
      t4 = cn("bg-sidebar text-sidebar-foreground flex h-full w-[var(--sidebar-width)] flex-col", className);
      $[7] = className;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] !== children || $[10] !== props || $[11] !== t4) {
      t5 = <div data-slot="sidebar" className={t4} {...props}>{children}</div>;
      $[9] = children;
      $[10] = props;
      $[11] = t4;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    return t5;
  }
  const t4 = state === "collapsed" ? collapsible : "";
  const t5 = variant === "floating" || variant === "inset" ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+1rem)]" : "group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)]";
  let t6;
  if ($[13] !== t5) {
    t6 = cn("relative w-[var(--sidebar-width)] bg-transparent [transition:width_240ms_cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none block", "group-data-[collapsible=offcanvas]:w-0", "group-data-[side=right]:rotate-180", t5);
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t6) {
    t7 = <div data-slot="sidebar-gap" className={t6} />;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = side === "left" ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]" : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]";
  const t9 = variant === "floating" || variant === "inset" ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+1rem+2px)]" : "group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)] group-data-[side=right]:border-l";
  let t10;
  if ($[17] !== className || $[18] !== t8 || $[19] !== t9) {
    t10 = cn("fixed top-[var(--migrate-ci-header-height,0px)] bottom-0 z-10 w-[var(--sidebar-width)] [transition:width_240ms_cubic-bezier(0.22,1,0.36,1),left_240ms_cubic-bezier(0.22,1,0.36,1),right_240ms_cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none flex", t8, t9, className);
    $[17] = className;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== children) {
    t11 = <div data-sidebar="sidebar" data-slot="sidebar-inner" className="bg-sidebar group-data-[variant=floating]:border-sidebar-border flex h-full w-full flex-col group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:shadow-sm border-r border-[var(--color-border)] dark:border-[var(--color-border)]">{children}</div>;
    $[21] = children;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== props || $[24] !== t10 || $[25] !== t11) {
    t12 = <div data-slot="sidebar-container" className={t10} {...props}>{t11}</div>;
    $[23] = props;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== side || $[28] !== state || $[29] !== t12 || $[30] !== t4 || $[31] !== t7 || $[32] !== variant) {
    t13 = <div className="group peer text-sidebar-foreground mr-px" data-state={state} data-collapsible={t4} data-variant={variant} data-side={side} data-slot="sidebar">{t7}{t12}</div>;
    $[27] = side;
    $[28] = state;
    $[29] = t12;
    $[30] = t4;
    $[31] = t7;
    $[32] = variant;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  return t13;
}
function SidebarTrigger(t0) {
  const $ = _c(17);
  let className;
  let onClick;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onClick;
    $[3] = props;
  } else {
    className = $[1];
    onClick = $[2];
    props = $[3];
  }
  const {
    toggleSidebar,
    state
  } = useSidebar();
  let t1;
  if ($[4] !== className) {
    t1 = cn("size-7", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== onClick || $[7] !== toggleSidebar) {
    t2 = event => {
      onClick?.(event);
      toggleSidebar();
    };
    $[6] = onClick;
    $[7] = toggleSidebar;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== state) {
    t3 = state === "collapsed" ? <ChevronRight /> : <ChevronLeft />;
    $[9] = state;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="sr-only">Toggle Sidebar</span>;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== props || $[13] !== t1 || $[14] !== t2 || $[15] !== t3) {
    t5 = <Button data-sidebar="trigger" data-slot="sidebar-trigger" variant="subtle" size="icon" className={t1} onClick={t2} {...props}>{t3}{t4}</Button>;
    $[12] = props;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}
function SidebarRail(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    toggleSidebar
  } = useSidebar();
  let t1;
  if ($[3] !== className) {
    t1 = cn("hover:after:bg-sidebar-border absolute inset-y-0 z-20 flex w-4 -translate-x-1/2 transition-none ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:left-1/2 after:w-[2px]", "[[data-side=left][data-state=expanded]_&]:cursor-w-resize [[data-side=left][data-state=collapsed]_&]:cursor-e-resize", "[[data-side=right][data-state=expanded]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize", "hover:group-data-[collapsible=offcanvas]:bg-sidebar group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full", "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2", "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1 || $[7] !== toggleSidebar) {
    t2 = <button data-sidebar="rail" data-slot="sidebar-rail" aria-label="Toggle Sidebar" tabIndex={-1} onClick={toggleSidebar} title="Toggle Sidebar" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = toggleSidebar;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function SidebarInset(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("bg-white relative flex w-full flex-1 flex-col dark:bg-neutral-950 min-h-0", "md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <main data-slot="sidebar-inset" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarInput(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("bg-white h-8 w-full shadow-none dark:bg-neutral-950", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <Input data-slot="sidebar-input" data-sidebar="input" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarHeader(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-col gap-2 p-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="sidebar-header" data-sidebar="header" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarFooter(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-col gap-2 p-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="sidebar-footer" data-sidebar="footer" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarSeparator(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("bg-sidebar-border mx-2 w-auto", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <Separator data-slot="sidebar-separator" data-sidebar="separator" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarContent(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="sidebar-content" data-sidebar="content" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarGroup(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("relative flex w-full min-w-0 flex-col p-2 rounded-md border border-transparent focus-visible:outline-none focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="sidebar-group" data-sidebar="group" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarGroupLabel(t0) {
  const $ = _c(10);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      asChild: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const asChild = t1 === undefined ? false : t1;
  const Comp = asChild ? Slot : "div";
  let t2;
  if ($[4] !== className) {
    t2 = cn("text-sidebar-foreground/70 ring-sidebar-ring flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium outline-hidden transition-none border border-transparent focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0 [&>svg]:size-4 [&>svg]:shrink-0", "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== Comp || $[7] !== props || $[8] !== t2) {
    t3 = <Comp data-slot="sidebar-group-label" data-sidebar="group-label" className={t2} {...props} />;
    $[6] = Comp;
    $[7] = props;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function SidebarGroupAction(t0) {
  const $ = _c(10);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      asChild: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const asChild = t1 === undefined ? false : t1;
  const Comp = asChild ? Slot : "button";
  let t2;
  if ($[4] !== className) {
    t2 = cn("text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-none border border-transparent focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0 [&>svg]:size-4 [&>svg]:shrink-0", "after:absolute after:-inset-2 md:after:hidden", "group-data-[collapsible=icon]:hidden", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== Comp || $[7] !== props || $[8] !== t2) {
    t3 = <Comp data-slot="sidebar-group-action" data-sidebar="group-action" className={t2} {...props} />;
    $[6] = Comp;
    $[7] = props;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function SidebarGroupContent(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("w-full text-sm", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="sidebar-group-content" data-sidebar="group-content" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarMenu(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex w-full min-w-0 flex-col gap-0.5", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ul data-slot="sidebar-menu" data-sidebar="menu" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarMenuItem(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("group/menu-item relative", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <li data-slot="sidebar-menu-item" data-sidebar="menu-item" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
const sidebarMenuButtonVariants = cva("peer/menu-button type-body-s-regular flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left outline-hidden ring-sidebar-ring transition-none border border-transparent hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0", {
  variants: {
    variant: {
      default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
      outline: "bg-white shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))] dark:bg-neutral-950"
    },
    size: {
      default: "h-8",
      sm: "h-7",
      lg: "h-12 group-data-[collapsible=icon]:p-0!"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});
function SidebarMenuButton(t0) {
  const $ = _c(28);
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let tooltip;
  if ($[0] !== t0) {
    ({
      asChild: t1,
      isActive: t2,
      variant: t3,
      size: t4,
      tooltip,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = tooltip;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    tooltip = $[7];
  }
  const asChild = t1 === undefined ? false : t1;
  const isActive = t2 === undefined ? false : t2;
  const variant = t3 === undefined ? "default" : t3;
  const size = t4 === undefined ? "default" : t4;
  const Comp = asChild ? Slot : "button";
  const {
    isMobile,
    state
  } = useSidebar();
  let t5;
  if ($[8] !== className || $[9] !== size || $[10] !== variant) {
    t5 = cn(sidebarMenuButtonVariants({
      variant,
      size
    }), className);
    $[8] = className;
    $[9] = size;
    $[10] = variant;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== Comp || $[13] !== isActive || $[14] !== props || $[15] !== size || $[16] !== t5) {
    t6 = <Comp data-slot="sidebar-menu-button" data-sidebar="menu-button" data-size={size} data-active={isActive} className={t5} {...props} />;
    $[12] = Comp;
    $[13] = isActive;
    $[14] = props;
    $[15] = size;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const button = t6;
  if (!tooltip) {
    return button;
  }
  if (typeof tooltip === "string") {
    let t7;
    if ($[18] !== tooltip) {
      t7 = {
        children: tooltip
      };
      $[18] = tooltip;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    tooltip = t7;
  }
  let t7;
  if ($[20] !== button) {
    t7 = <TooltipTrigger asChild={true}>{button}</TooltipTrigger>;
    $[20] = button;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const t8 = state !== "collapsed" || isMobile;
  let t9;
  if ($[22] !== t8 || $[23] !== tooltip) {
    t9 = <TooltipContent side="right" align="center" hidden={t8} {...tooltip} className="z-100" />;
    $[22] = t8;
    $[23] = tooltip;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t7 || $[26] !== t9) {
    t10 = <Tooltip>{t7}{t9}</Tooltip>;
    $[25] = t7;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}
function SidebarMenuAction(t0) {
  const $ = _c(12);
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      asChild: t1,
      showOnHover: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const asChild = t1 === undefined ? false : t1;
  const showOnHover = t2 === undefined ? false : t2;
  const Comp = asChild ? Slot : "button";
  const t3 = showOnHover && "peer-data-[active=true]/menu-button:text-sidebar-accent-foreground group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 md:opacity-0";
  let t4;
  if ($[5] !== className || $[6] !== t3) {
    t4 = cn("text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground peer-hover/menu-button:text-sidebar-accent-foreground absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-none border border-transparent focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0 [&>svg]:size-4 [&>svg]:shrink-0", "after:absolute after:-inset-2 md:after:hidden", "peer-data-[size=sm]/menu-button:top-1", "peer-data-[size=default]/menu-button:top-1.5", "peer-data-[size=lg]/menu-button:top-2.5", "group-data-[collapsible=icon]:hidden", t3, className);
    $[5] = className;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== Comp || $[9] !== props || $[10] !== t4) {
    t5 = <Comp data-slot="sidebar-menu-action" data-sidebar="menu-action" className={t4} {...props} />;
    $[8] = Comp;
    $[9] = props;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function SidebarMenuBadge(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-sidebar-foreground pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums select-none", "peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground", "peer-data-[size=sm]/menu-button:top-1", "peer-data-[size=default]/menu-button:top-1.5", "peer-data-[size=lg]/menu-button:top-2.5", "group-data-[collapsible=icon]:hidden", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="sidebar-menu-badge" data-sidebar="menu-badge" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarMenuSkeleton(t0) {
  const $ = _c(17);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      showIcon: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const showIcon = t1 === undefined ? false : t1;
  const [width] = React.useState(_temp5);
  let t2;
  if ($[4] !== className) {
    t2 = cn("flex h-8 items-center gap-2 rounded-md px-2", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== showIcon) {
    t3 = showIcon && <Skeleton className="size-4 rounded-md" data-sidebar="menu-skeleton-icon" />;
    $[6] = showIcon;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== width) {
    t4 = {
      "--skeleton-width": width
    };
    $[8] = width;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = t4 as React.CSSProperties;
  let t6;
  if ($[10] !== t5) {
    t6 = <Skeleton className="h-4 max-w-[var(--skeleton-width)] flex-1" data-sidebar="menu-skeleton-text" style={t5} />;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== props || $[13] !== t2 || $[14] !== t3 || $[15] !== t6) {
    t7 = <div data-slot="sidebar-menu-skeleton" data-sidebar="menu-skeleton" className={t2} {...props}>{t3}{t6}</div>;
    $[12] = props;
    $[13] = t2;
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
function _temp5() {
  return `${Math.floor(Math.random() * 40) + 50}%`;
}
function SidebarMenuSub(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("border-sidebar-border mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l px-2.5 py-0.5", "group-data-[collapsible=icon]:hidden", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ul data-slot="sidebar-menu-sub" data-sidebar="menu-sub" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarMenuSubItem(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("group/menu-sub-item relative", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <li data-slot="sidebar-menu-sub-item" data-sidebar="menu-sub-item" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarMenuSubButton(t0) {
  const $ = _c(16);
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      asChild: t1,
      size: t2,
      isActive: t3,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
  }
  const asChild = t1 === undefined ? false : t1;
  const size = t2 === undefined ? "md" : t2;
  const isActive = t3 === undefined ? false : t3;
  const Comp = asChild ? Slot : "a";
  const t4 = size === "sm" && "text-xs";
  const t5 = size === "md" && "text-sm";
  let t6;
  if ($[6] !== className || $[7] !== t4 || $[8] !== t5) {
    t6 = cn("text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground [&>svg]:text-sidebar-accent-foreground flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 outline-hidden border border-transparent focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0", "data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground", t4, t5, "group-data-[collapsible=icon]:hidden", className);
    $[6] = className;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== Comp || $[11] !== isActive || $[12] !== props || $[13] !== size || $[14] !== t6) {
    t7 = <Comp data-slot="sidebar-menu-sub-button" data-sidebar="menu-sub-button" data-size={size} data-active={isActive} className={t6} {...props} />;
    $[10] = Comp;
    $[11] = isActive;
    $[12] = props;
    $[13] = size;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
export { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupAction, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarInput, SidebarInset, SidebarMenu, SidebarMenuAction, SidebarMenuBadge, SidebarMenuButton, SidebarMenuItem, SidebarMenuSkeleton, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, SidebarProvider, SidebarRail, SidebarSeparator, SidebarTrigger, useSidebar };